import { useEffect, useState } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';

import { useRendererActions } from '@confluence/renderer-actions/entry-points/RendererActionsContext.ts';
import { useFullPageEditorApiState } from '@confluence/full-page-editor-store';

export type UsePageAdfResult = {
	/** The ADF document, or null if not yet available from either source. */
	adf: DocNode | null;
};

/**
 * Returns the page ADF from whichever in-memory source is available:
 *
 * 1. RendererActionsContext.pageAdf — synchronous, set by the renderer on mount.
 *    Preferred because it is always up-to-date for normal and live-page view modes.
 * 2. Editor API requestDocument — used for live pages where the editor is mounted.
 *    Captured once at hook mount time (one-shot snapshot).
 *
 * Returns { adf: null } until one of the sources provides a document.
 */
export function usePageAdf(): UsePageAdfResult {
	const { pageAdf: rendererPageAdf } = useRendererActions();
	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();
	const [editorAdf, setEditorAdf] = useState<DocNode | null>(null);

	useEffect(() => {
		if (rendererPageAdf || !fullPageEditorPresetAPI) {
			return;
		}
		fullPageEditorPresetAPI.core.actions.requestDocument(
			(doc) => {
				if (doc) {
					setEditorAdf(doc as DocNode);
				}
			},
			{ alwaysFire: true },
		);
		// requestDocument is a one-shot callback; we only need to call it when the
		// editor API first becomes available (or when renderer ADF is absent).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fullPageEditorPresetAPI]);

	const adf = rendererPageAdf ?? editorAdf;
	return { adf: adf ?? null };
}
